import { Formik, Form } from "formik";
import * as Yup from "yup";
import FormField from "components/v2/FormField";
import { IoIosArrowForward } from "react-icons/io";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import useOverlayStore from "store/overlayStore";
import Dialog from "components/Overlays/ResponsiveDialog";
import useAddSubscription from "api/SubscriptionsResource/useAddSubscription";
import { activeCurrencies } from "utils";

const AddSubscriptionPlan = () => {
	const initialValues = {
		name: "",
		duration: "",
		time_unit: "",
		cost: "",
		category: "",
		max_number_of_books: "",
		max_allocations_per_seat: "",
		picks_policy: "unlimited",
		max_picks_value: "",
		currency_symbol: "$",
		currency_code: activeCurrencies.USD.code,
		visibility: "internal",
	};

	const validate = Yup.object().shape({
		name: Yup.string().required("Name of plan required"),
		duration: Yup.number().min(1).required("Duration required"),
		time_unit: Yup.string().required("Time units required"),
		cost: Yup.number().min(0).required("Cost required"),
		category: Yup.string().required("Category required"),
		max_number_of_books: Yup.number().when("category", {
			is: "individual",
			then: (schema) =>
				schema
					.typeError("Enter maximum number of books")
					.min(1, "Enter maximum number of books")
					.required("Enter maximum number of books"),
			otherwise: (schema) => schema.nullable(),
		}),
		max_allocations_per_seat: Yup.number().when("category", {
			is: "institution",
			then: (schema) =>
				schema
					.typeError("Enter maximum allocations per seat")
					.min(1, "Enter maximum allocations per seat")
					.required("Enter maximum allocations per seat"),
			otherwise: (schema) => schema.nullable(),
		}),
	});

	const { showCreateSubscription, setShowCreateSubscription } = useOverlayStore();

	const { mutateAsync, isLoading } = useAddSubscription();

	const onSubmit = async (data) => {
		const isInstitutional = data.category === "institution";

		await mutateAsync({
			...data,
			cost: Number(data.cost),
			duration: Number(data.duration),
			max_number_of_books: isInstitutional
				? Number(data.max_allocations_per_seat || 1)
				: Number(data.max_number_of_books),
			max_allocations_per_seat: isInstitutional
				? Number(data.max_allocations_per_seat)
				: undefined,
			picks_policy: isInstitutional ? "unlimited" : undefined,
			max_picks_value: isInstitutional ? undefined : data.max_picks_value || undefined,
		});
	};

	return (
		<Dialog
			visible={showCreateSubscription}
			style={{ width: "50vw" }}
			header={
				<div className="text-xl font-sans font-bold sticky top-4 z-20 bg-white mr-2">
					<p className="">Create subscription plan</p>
				</div>
			}
			onHide={() => setShowCreateSubscription(false)}
			position="top"
			draggable={false}
			contentClassName="rounded-b-xl "
			className="rounded-xl relative"
		>
			<Formik initialValues={initialValues} validationSchema={validate} onSubmit={onSubmit}>
				{({ values }) => {
					const isInstitutional = values.category === "institution";
					return (
						<Form className="mt-6 space-y-6">
							<FormField type="text" name="name" label="Subscription name *" />
							<FormField
								type="select"
								name="category"
								label="Category *"
								options={[
									{ label: "Individual", value: "individual" },
									{ label: "Institution", value: "institution" },
								]?.map((role) => ({
									label: role.label,
									value: role.value,
								}))}
							/>

							<FormField
								type="number"
								name="duration"
								label="Duration*"
								helperText="Numeric number e.g 7"
							/>

							<FormField
								type="select"
								name="time_unit"
								label="Time units (e.g days) *"
								options={[
									{ label: "Hours", value: "hours" },
									{ label: "Days", value: "days" },
									{ label: "Weeks", value: "weeks" },
									{ label: "Months", value: "months" },
									{ label: "Years", value: "years" },
								]?.map((role) => ({
									label: role.label,
									value: role.value,
								}))}
							/>

							<FormField
								type="number"
								min="0"
								name="cost"
								step="0.01"
								label={isInstitutional ? "Price per seat/student *" : "Cost *"}
								helperText={
									isInstitutional
										? "This price is multiplied by the number of seats bought during checkout."
										: undefined
								}
							/>

							{isInstitutional ? (
								<>
									<FormField
										type="number"
										min="1"
										name="max_allocations_per_seat"
										label="Maximum allocations per seat *"
										helperText="Maximum number of picked books each seat/student can be allocated."
									/>

									<div className="rounded-xl border border-akgreen/20 bg-akgreen/5 p-4 text-sm text-gray-700">
										<p className="font-semibold text-akgreenest">Picks bank</p>
										<p className="mt-1">
											Institution plans use an <span className="font-semibold">Unlimited</span>{" "}
											picks bank. Admins can keep adding books after payment, while student access is
											still controlled by seat capacity and allocations.
										</p>
									</div>
								</>
							) : (
								<FormField
									type="number"
									min="0"
									name="max_number_of_books"
									label="Maximum number of books for this package *"
									helperText="e.g 7"
								/>
							)}

							<FormField
								type="select"
								name="visibility"
								label="Visibility *"
								helperText="Internal: visible in library app | Outbound: hidden from library, for external apps only"
								options={[
									{ label: "Internal", value: "internal" },
									{ label: "Outbound", value: "outbound" },
								]?.map((option) => ({
									label: option.label,
									value: option.value,
								}))}
							/>

							<div className=" flex justify-center border-t border-gray-300 pt-2">
								<button
									type="submit"
									className="px-4 py-2 rounded-full bg-akgreenest text-white flex gap-1 items-center justify-center"
								>
									{isLoading ? (
										<>
											Submitting <AiOutlineLoading3Quarters size={20} className="animate-spin" />
										</>
									) : (
										<>
											Submit <IoIosArrowForward size={20} />
										</>
									)}
								</button>
							</div>
						</Form>
					);
				}}
			</Formik>
		</Dialog>
	);
};

export default AddSubscriptionPlan;
